// Type aliases for cleaner imports of generated activity-related types
export type {
  activities_ActivityResponse as ActivityResponse,
  activities_BatchCreateError as BatchCreateError,
  activities_BatchCreateRequest as BatchCreateRequest,
  activities_BatchCreateResponse as BatchCreateResponse,
  // #1156: `activities_ChildCurrentStateDTO` is not a generated type — the real
  // one is `activities_ChildCurrentStateResponse`. The public alias name is kept.
  activities_ChildCurrentStateResponse as ChildCurrentStateDTO,
  activities_ChildCurrentStateResponse as ChildCurrentStateResponse,
  activities_ChildSummaryDTO as ChildSummaryDTO,
  activities_CommentDTO as CommentDTO,
  activities_CreateActivityDTO as CreateActivityDTO,
  activities_ListActivitiesResponse as ListActivitiesResponse,
  activities_UpdateActivityRequest as UpdateActivityRequest,
  activities_UserSummaryDTO as UserSummaryDTO,
} from '@/api';

// Export ActivityType enum from generated API
export { models_ActivityType as ActivityType } from '@/api';

// Re-import for use in getActivityTypeLabels
import { models_ActivityType as ActivityType } from '@/api';
import type { I18n } from '@lingui/core';

/** The slice of Lingui's i18n the label factory needs, so detail renderers with a narrow i18n prop can use it. */
type ActivityLabelTranslator = Pick<I18n, '_'> | { _: (d: { id: string; message: string }) => string };

/**
 * Factory function that returns activity type labels with explicit i18n IDs.
 * This pattern allows for type-safe translations with explicit message IDs.
 *
 * @param i18n - The Lingui i18n instance from useLingui()
 * @returns Record mapping activity types to their translated labels
 *
 * @example
 * const { i18n } = useLingui();
 * const labels = getActivityTypeLabels(i18n);
 * const label = labels[activity.type]; // "Check In", "Feeding - Milk", etc.
 */
export const getActivityTypeLabels = (i18n: ActivityLabelTranslator): Record<string, string> => ({
  [ActivityType.ActivityTypeCheckIn]: i18n._({
    id: 'activities.types.checkIn',
    message: 'Check In',
  }),
  [ActivityType.ActivityTypeCheckOut]: i18n._({
    id: 'activities.types.checkOut',
    message: 'Check Out',
  }),
  [ActivityType.ActivityTypeToiletPee]: i18n._({
    id: 'activities.types.toiletPee',
    message: 'Toilet - Pee',
  }),
  [ActivityType.ActivityTypeToiletPoo]: i18n._({
    id: 'activities.types.toiletPoo',
    message: 'Toilet - Poo',
  }),
  [ActivityType.ActivityTypeToiletNothing]: i18n._({
    id: 'activities.types.toiletNothing',
    message: 'Toilet - Nothing',
  }),
  [ActivityType.ActivityTypeFeedingMilk]: i18n._({
    id: 'activities.types.feedingMilk',
    message: 'Eating - Milk',
  }),
  [ActivityType.ActivityTypeFeedingPorridge]: i18n._({
    id: 'activities.types.feedingPorridge',
    message: 'Eating - Porridge',
  }),
  [ActivityType.ActivityTypeFeedingSolid]: i18n._({
    id: 'activities.types.feedingSolid',
    message: 'Eating - Solid Food',
  }),
  [ActivityType.ActivityTypeFeedingSnack]: i18n._({
    id: 'activities.types.feedingSnack',
    message: 'Eating - Snack',
  }),
  [ActivityType.ActivityTypeFeedingBreastmilk]: i18n._({
    id: 'activities.types.feedingBreastmilk',
    message: 'Eating - Breastmilk',
  }),
  [ActivityType.ActivityTypeFeedingRefused]: i18n._({
    id: 'activities.types.feedingRefused',
    message: 'Eating - Refused Food',
  }),
  [ActivityType.ActivityTypeNapStart]: i18n._({
    id: 'activities.types.napStart',
    message: 'Nap Start',
  }),
  [ActivityType.ActivityTypeNapEnd]: i18n._({
    id: 'activities.types.napEnd',
    message: 'Nap End',
  }),
  [ActivityType.ActivityTypeHealthAccident]: i18n._({
    id: 'activities.types.healthAccident',
    message: 'Health - Accident',
  }),
  [ActivityType.ActivityTypeHealthIllness]: i18n._({
    id: 'activities.types.healthIllness',
    message: 'Health - Illness',
  }),
  [ActivityType.ActivityTypeHealthMedication]: i18n._({
    id: 'activities.types.healthMedication',
    message: 'Health - Medication',
  }),
  [ActivityType.ActivityTypePicture]: i18n._({
    id: 'activities.types.picture',
    message: 'Picture',
  }),
  [ActivityType.ActivityTypeOutdoor]: i18n._({
    id: 'activities.types.outdoor',
    message: 'Outdoor',
  }),
  [ActivityType.ActivityTypeIndoor]: i18n._({
    id: 'activities.types.indoor',
    message: 'Indoor',
  }),
  [ActivityType.ActivityTypeNote]: i18n._({
    id: 'activities.types.note',
    message: 'Note',
  }),
  [ActivityType.ActivityTypeMoodCheck]: i18n._({
    id: 'activities.types.moodCheck',
    message: 'Mood Check',
  }),
});

// List parameters for activity queries
export interface ActivityListParams {
  page?: number; // Default: 1, Min: 1
  page_size?: number; // Default: 20, Min: 1, Max: 100
  child_id?: string;
  type?: string; // ActivityType value
  date_from?: Date; // Will be converted to RFC3339
  date_to?: Date; // Will be converted to RFC3339
  group_id?: string; // Children currently allocated to this group
  childcare_id?: string; // Children currently allocated to a group of this childcare
}
